/* SPEC-504 — the engaged-time counter behind hooks/useEngagedTime.ts. Pure (no React, no DOM
 * globals): the hook passes the environment in, so the honesty rules are testable in Node.
 *
 * Rules:
 *   - a second counts only while the tab is visible AND the window is focused AND the person
 *     has moved, typed, scrolled or touched within the last 60 s (IDLE_MS);
 *   - seconds are posted every 15 s (HEARTBEAT_MS), and on a screen change or when the tab is
 *     hidden/closed (final=true, sent with keepalive by the hook);
 *   - nothing is posted when nothing was engaged; only the screen path and its `tab` are sent,
 *     never what was typed and never any other query parameter. */

export const HEARTBEAT_MS = 15_000;
export const IDLE_MS = 60_000;
/** A tick gap larger than this (a throttled background timer) is not counted as engaged. */
export const MAX_TICK_MS = 2_000;

export interface Screen { path: string; tab: string | null }

export interface EngagedEnv {
  now: () => number;
  visible: () => boolean;
  focused: () => boolean;
}

export type EngagedPost = (screen: Screen, seconds: number, final: boolean) => void;

/** Collapse identifiers so a product code is never a "screen" (mirrors measure/journeys.py). */
export function screenPath(pathname: string): string {
  if (/^\/products\/[^/]+$/.test(pathname)) return '/products/:code';
  if (/^\/oos\/[^/]+$/.test(pathname)) return '/oos/:weeks';
  if (/^\/invite\/[^/]+$/.test(pathname)) return '/invite/:token';
  return pathname;
}

export function createEngagedTracker(env: EngagedEnv, post: EngagedPost) {
  let lastInput = Number.NEGATIVE_INFINITY;
  let lastTick = env.now();
  let lastFlush = env.now();
  let accMs = 0;
  let screen: Screen = { path: '', tab: null };

  const engagedAt = (t: number) => env.visible() && env.focused() && t - lastInput <= IDLE_MS;

  function flush(final: boolean) {
    const seconds = Math.floor(accMs / 1000);
    lastFlush = env.now();
    if (seconds < 1 || !screen.path) return;
    accMs -= seconds * 1000;
    post(screen, seconds, final);
  }

  return {
    /** Any mouse/key/scroll/touch input. The key pressed is never read. */
    input() { lastInput = env.now(); },
    /** Call about once a second. */
    tick() {
      const t = env.now();
      const dt = t - lastTick;
      lastTick = t;
      if (dt > 0 && dt <= MAX_TICK_MS && engagedAt(t)) accMs += dt;
      if (t - lastFlush >= HEARTBEAT_MS) flush(false);
    },
    /** A route change: seconds so far belong to the old screen. */
    setScreen(next: Screen) {
      if (next.path === screen.path && next.tab === screen.tab) return;
      flush(false);
      accMs = 0;
      screen = next;
    },
    /** The tab is being hidden or closed. */
    hide() {
      flush(true);
      accMs = 0;
    },
    /** For tests and diagnostics. */
    pendingMs() { return accMs; },
  };
}
